import React, { useEffect, useRef, useState } from "react";
import { Avatar, Button, TextField, Typography, CircularProgress, Box } from '@material-ui/core/';
import LockOutlinedIcon from '@material-ui/icons/LockOutlined';
import { useTranslation } from "react-i18next";
import { Link as RouterLink, useParams } from "react-router-dom";
import Alert from "@material-ui/lab/Alert";

import { repliValidator } from "../../../components";
import ChildrenLoading from "../../../components/ChildrenLoading";

import useStyles from "./PasswordReset.style";

const LoginTextField = props => {
    return <TextField
        variant="outlined"
        margin="normal"
        required
        fullWidth
        {...props}
    />
}

const PasswordReset = () => {
    const classes = useStyles();
    const { t } = useTranslation();
    const { token } = useParams();

    const [loading, setLoading] = useState(true);
    const [submitted, setSubmitted] = useState(false);
    const [sending, setSending] = useState(false);
    const [saveResult, setSaveResult] = useState();

    const [isValidToken, setIsValidToken] = useState(true);
    const [password, setPassword] = useState('');
    const [passwordConfirmation, setPasswordConfirmation] = useState('');

    const validator = useRef(repliValidator({
        validators: {
            password_confirmation: {
                message: t('common.message.validation.passwordConfirmation'),
                rule: (val, params, validator) => {
                    return val === params[0]
                },
                required: true
            }
        }
    }));

    useEffect(() => {
        const validateToken = async () => {
            try {
                let response = await fetch(`${process.env.REACT_APP_API_URL}/${process.env.REACT_APP_API_AUTH_PASSWORDRESETTOKENVALIDATE}`, {
                    method: 'post',
                    body: new URLSearchParams({
                        token: token
                    })
                });
                if (!response.ok) setIsValidToken(false);
            } catch (error) {
                console.log(error);
            } finally {
                setLoading(false);
            };
        };

        validateToken();
    }, [token]);

    const handleSubmit = async e => {
        e.preventDefault();
        setSubmitted(true);

        const isValid = validator.current.allValid();
        if (!isValid) {
            validator.current.showMessages();
            return;
        }

        setSending(true);
        try {
            let response = await fetch(`${process.env.REACT_APP_API_URL}/${process.env.REACT_APP_API_AUTH_PASSWORDRESET}`, {
                method: 'post',
                body: new URLSearchParams({
                    token: token,
                    password: password,
                    passwordConfirmation: passwordConfirmation
                })
            });
            if (!response.ok) {
                let json = await response.json();
                setSaveResult({ severity: 'error', message: json.message });
                return;
            };
            setSaveResult({ severity: 'success', message: t('passwordReset.message.success') });
        } catch (error) {
            console.log(error.message);
            setSaveResult({ severity: 'error', message: t('passwordReset.message.error') });
        } finally {
            setSending(false);
        };
    };

    if (loading) return <ChildrenLoading />

    return (
        <div className={classes.paper}>
            <Avatar className={classes.avatar}>
                <LockOutlinedIcon />
            </Avatar>
            <Typography component="h1" variant="h5">
                {t('passwordReset.title')}
            </Typography>
            {!isValidToken &&
                <>
                    <Typography variant="h3" className={classes.invalidTokenMessage}>{t('common.message.validation.invalidToken')}</Typography>
                    <Box className={classes.buttonContainer}>
                        <Button
                            component={RouterLink}
                            to="/provider/login"
                            fullWidth
                            variant="contained"
                            color="primary"
                        >
                            {t('passwordReset.buttonLabel.signin')}
                        </Button>
                    </Box>
                </>
            }
            {isValidToken &&
                <form className={classes.form} noValidate onSubmit={handleSubmit}>
                    <LoginTextField
                        label={t('common.form.label.password')}
                        type="password"
                        autoFocus
                        error={submitted && !validator.current.fieldValid('password')}
                        helperText={validator.current.message('password', password, 'required|min:8')}
                        onChange={e => setPassword(e.target.value.trim())}
                        value={password}
                    />
                    <LoginTextField
                        label={t('common.form.label.passwordConfirmation')}
                        type="password"
                        error={submitted && !validator.current.fieldValid('passwordConfirmation')}
                        helperText={validator.current.message('passwordConfirmation', passwordConfirmation, `password_confirmation:${password}`)}
                        onChange={e => setPasswordConfirmation(e.target.value.trim())}
                        value={passwordConfirmation}
                    />
                    {
                        saveResult &&
                        <Alert severity={saveResult.severity}>{saveResult.message}</Alert>
                    }
                    <Button
                        type="submit"
                        fullWidth
                        variant="contained"
                        color="primary"
                        className={classes.submit}
                        disabled={sending}
                    >
                        {t('passwordReset.buttonLabel.changePassword')}
                        {sending && <CircularProgress size={24} className={classes.buttonProgress} />}
                    </Button>
                    <Button
                        fullWidth
                        variant="outlined"
                        color="primary"
                        component={RouterLink}
                        to="/provider/login"
                    >
                        {t('passwordReset.buttonLabel.cancel')}
                    </Button>
                </form>
            }
        </div>
    )
};

export default PasswordReset;